<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';

import type { Component as VueComponent } from 'vue';

import {
  subscribeToCustomEvent,
  unsubscribeFromCustomEvent,
  emitCustomEvent
} from '@satellite/composables/useEventBasedDialog';

const {
  eventName,
  component,
  showBootstrapFeedback = true
} = defineProps<{
  eventName: string;
  component: VueComponent;
  showBootstrapFeedback?: boolean;
}>();

const shouldMount = ref(false);
const pendingProps = ref<CustomEvent['detail'] | null>(null);

const isBootstrapping = computed(() => pendingProps.value !== null);

const handleTriggerEvent = (event: CustomEvent) => {
  pendingProps.value = event.detail;

  if (!shouldMount.value) {
    shouldMount.value = true;
  }
};

const handleReady = () => {
  unsubscribeFromCustomEvent(eventName, handleTriggerEvent);
  emitCustomEvent(eventName, pendingProps.value);
  pendingProps.value = null;
};

onMounted(() => {
  subscribeToCustomEvent(eventName, handleTriggerEvent);
});

onUnmounted(() => {
  unsubscribeFromCustomEvent(eventName, handleTriggerEvent);
});
</script>

<template>
  <div class="event-triggered-lazy-dialog-host">
    <Teleport to="body">
      <v-overlay
        v-if="showBootstrapFeedback && isBootstrapping"
        :model-value="true"
        class="align-center justify-center"
        persistent
        data-testid="event-triggered-lazy-dialog-host-loading"
        :z-index="2399">
        <v-progress-circular indeterminate color="primary" />
      </v-overlay>
    </Teleport>
    <component :is="component" v-if="shouldMount" @ready="handleReady" />
  </div>
</template>

<style scoped>
.event-triggered-lazy-dialog-host {
  display: contents;
}
</style>
